import React, { useState, useEffect, useRef, useCallback } from "react";

const G = "#0077B5";
const B = "#E1E9EE";
const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);

const TOPIC_LABELS = {};

export default function ContentSourcesRedesign({ user, setUser, CATEGORIES }) {
  const [priorityList, setPriorityList] = useState([]);
  const [topicSelection, setTopicSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddRss, setShowAddRss] = useState(false);
  const [rssUrl, setRssUrl] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  // Draft subtopic state (mirrors Voice section draft pattern)
  const [draftSubtopics, setDraftSubtopics] = useState(null);
  const [subtopicSaving, setSubtopicSaving] = useState(false);

  const plan = (user?.plan || "free").toLowerCase();
  const isPro = plan === "authority_pro";
  const rssLimits = { free: 0, presence: 2, authority: 3, authority_pro: 3, team: 3, agency: 3 };
  const maxRss = rssLimits[plan] || 2;

  // Build topic labels from CATEGORIES
  useEffect(() => {
    if (CATEGORIES) {
      CATEGORIES.forEach(c => { TOPIC_LABELS[c.id] = c.label; });
    }
  }, [CATEGORIES]);

  // Load content sources
  useEffect(() => {
    const st = localStorage.getItem("nectar_st");
    fetch(`${API_BASE}/app/api/user/content-sources`, {
      headers: { "X-Session-Token": st },
    })
      .then(r => r.json())
      .then(d => {
        setPriorityList(d.priority_list || []);
        setTopicSelection(d.topic_selection || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const topicCount = priorityList.filter(i => i.type === "topic").length;
  const rssCount = priorityList.filter(i => i.type === "rss").length;
  const selectedTopicIds = priorityList.filter(i => i.type === "topic").map(i => i.id);
  const unselectedTopics = (CATEGORIES || []).filter(c => !selectedTopicIds.includes(c.id));

  // Save (debounced) — used by structural changes only
  const save = useCallback((list, ts) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      const st = localStorage.getItem("nectar_st");
      await fetch(`${API_BASE}/app/api/user/content-sources`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Session-Token": st },
        body: JSON.stringify({ priority_list: list, topic_selection: ts || topicSelection }),
      });
      setSaving(false);
    }, 600);
  }, [topicSelection]);

  const maxTopics = 5;
  const isEditing = expandedTopic !== null;

  const moveItem = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= priorityList.length) return;
    setExpandedTopic(null);
    setDraftSubtopics(null);
    const newList = [...priorityList];
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setPriorityList(newList);
    save(newList);
  };

  const removeItem = (idx) => {
    const item = priorityList[idx];
    if (item.type === "topic" && topicCount <= 3) {
      alert("You need at least 3 topics. Add another topic first.");
      return;
    }
    if (!window.confirm(`Remove this ${item.type === "topic" ? "topic" : "RSS feed"} from your content sources?`)) return;
    const newList = priorityList.filter((_, i) => i !== idx);
    setPriorityList(newList);
    if (item.type === "topic") {
      const newTs = { ...topicSelection };
      delete newTs[item.id];
      setTopicSelection(newTs);
      save(newList, newTs);
    } else {
      save(newList);
    }
  };

  const addTopic = (topicId) => {
    if (selectedTopicIds.includes(topicId)) return;
    if (topicCount >= 5) { alert("Maximum 5 topics allowed."); return; }
    const newList = [...priorityList, { type: "topic", id: topicId }];
    const newTs = { ...topicSelection, [topicId]: "all" };
    setPriorityList(newList);
    setTopicSelection(newTs);
    save(newList, newTs);
  };

  const handleAddRss = async () => {
    let url = (rssUrl || "").trim();
    if (!url) return;
    if (!url.startsWith("http")) url = "https://" + url;
    if (rssCount >= maxRss) { alert(`Your plan allows up to ${maxRss} RSS feed(s).`); return; }

    setVerifying(true);
    setVerifyError(null);
    try {
      const st = localStorage.getItem("nectar_st");
      const r = await fetch(`${API_BASE}/app/api/user/verify-rss-feed`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Token": st },
        body: JSON.stringify({ feed_url: url }),
      });
      const d = await r.json();
      if (!r.ok || !d.valid) {
        setVerifyError(d.error || "Invalid RSS feed");
        setVerifying(false);
        return;
      }

      const insertResp = await fetch(`${API_BASE}/app/api/user/rss-feeds/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Token": st },
        body: JSON.stringify({ feed_url: url }),
      });

      const reloadResp = await fetch(`${API_BASE}/app/api/user/content-sources`, {
        headers: { "X-Session-Token": st },
      });
      const reloadData = await reloadResp.json();
      setPriorityList(reloadData.priority_list || []);
      setTopicSelection(reloadData.topic_selection || {});

      setRssUrl("");
      setShowAddRss(false);
    } catch {
      setVerifyError("Network error. Try again.");
    }
    setVerifying(false);
  };

  // ── Subtopic editor (draft pattern) ────────────────────────────────────────
  const openSubtopicEditor = (topicId) => {
    setDraftSubtopics(topicSelection[topicId] || "all");
    setExpandedTopic(topicId);
  };

  const cancelSubtopicEditor = () => {
    setDraftSubtopics(null);
    setExpandedTopic(null);
  };

  const saveSubtopicEditor = async () => {
    if (!expandedTopic || draftSubtopics === null) return;
    setSubtopicSaving(true);
    const newTs = { ...topicSelection, [expandedTopic]: draftSubtopics };
    setTopicSelection(newTs);
    const st = localStorage.getItem("nectar_st");
    await fetch(`${API_BASE}/app/api/user/content-sources`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Session-Token": st },
      body: JSON.stringify({ priority_list: priorityList, topic_selection: newTs }),
    });
    setSubtopicSaving(false);
    setDraftSubtopics(null);
    setExpandedTopic(null);
  };

  const toggleDraftSubtopic = (subtopicLabel) => {
    if (draftSubtopics === null) return;
    const cat = (CATEGORIES || []).find(c => c.id === expandedTopic);
    if (!cat) return;

    if (draftSubtopics === "all") {
      const allLabels = cat.subtopics.map(s => s.label).filter(l => l !== subtopicLabel);
      setDraftSubtopics(allLabels.length > 0 ? allLabels : "all");
    } else if (Array.isArray(draftSubtopics)) {
      const newArr = draftSubtopics.includes(subtopicLabel)
        ? draftSubtopics.filter(s => s !== subtopicLabel)
        : [...draftSubtopics, subtopicLabel];
      setDraftSubtopics(newArr.length === 0 ? "all" : newArr);
    } else {
      setDraftSubtopics([subtopicLabel]);
    }
  };

  // ── Row label helper ───────────────────────────────────────────────────────
  const topicRowLabel = (itemId) => {
    const cat = (CATEGORIES || []).find(c => c.id === itemId);
    if (!cat || !cat.subtopics) return TOPIC_LABELS[itemId] || itemId;
    const sel = topicSelection[itemId];
    const total = cat.subtopics.length;
    const activeNames = sel === "all" || !sel
      ? cat.subtopics.map(s => s.label)
      : Array.isArray(sel) ? sel : [];
    const active = activeNames.length;

    if (sel === "all" || !sel || active === total) {
      return { primary: cat.label, sub: null };
    } else if (active <= 3) {
      return { primary: cat.label, sub: activeNames.join(", ") };
    } else {
      return { primary: cat.label, sub: `${active} of ${total} active` };
    }
  };

  if (loading) return <div style={{ padding: 20, color: "#999" }}>Loading content sources...</div>;

  // Free plan: locked to in-house content
  if (plan === "free" || plan === "honey") {
    return (
      <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <SectionLabel>Content choices</SectionLabel>
        <div style={{ padding: "20px 16px", background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB" }}>
          <div style={{ fontSize: 14, color: "#333", marginBottom: 8 }}>
            Your free plan includes 2 weekly posts — BrandHives content adapted to your voice.
          </div>
          <div style={{ fontSize: 13, color: "#888" }}>
            Upgrade to Presence or Authority to choose your own topics and add RSS feeds.
          </div>
        </div>
      </div>
    );
  }

  const grayStyle = isEditing ? { pointerEvents: "none", opacity: 0.4, transition: "opacity 0.2s" } : {};

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <SectionLabel>{isPro ? "Backup topics" : "Content choices"}</SectionLabel>

      {isPro && (
        <div style={{ fontSize: 13, color: "#888", marginBottom: 12, lineHeight: 1.6 }}>
          Used when there is no news in your practice areas that week.
        </div>
      )}

      {/* Max topics indicator — prominent, at top */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#666" }}>
          {isPro ? "Choose backup topics for thin news weeks." : "Reorder to set priority. Top items get first pick for your weekly posts."}
          {saving && <span style={{ marginLeft: 8, color: G, fontSize: 12 }}>Saving...</span>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: topicCount >= maxTopics ? "#DC2626" : G, whiteSpace: "nowrap", marginLeft: 12 }}>
          {topicCount}/{maxTopics} topics
        </div>
      </div>

      {priorityList.map((item, idx) => {
        const isEditingThis = isEditing && expandedTopic === item.id;
        const rowGray = isEditing && !isEditingThis ? grayStyle : {};

        return (
          <div key={`${item.type}-${item.id}`}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", marginBottom: isEditingThis ? 0 : 6,
              background: isEditingThis ? "#F0F9FF" : "#fff",
              border: `1px solid ${isEditingThis ? G : "#E5E7EB"}`,
              borderRadius: isEditingThis ? "8px 8px 0 0" : 8,
              ...rowGray,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: G, minWidth: 24, textAlign: "center" }}>{idx + 1}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", fontSize: 10, color: idx === 0 ? "#ddd" : "#666", padding: 0, lineHeight: 1 }}>&#9650;</button>
                <button onClick={() => moveItem(idx, 1)} disabled={idx === priorityList.length - 1} style={{ background: "none", border: "none", cursor: idx === priorityList.length - 1 ? "default" : "pointer", fontSize: 10, color: idx === priorityList.length - 1 ? "#ddd" : "#666", padding: 0, lineHeight: 1 }}>&#9660;</button>
              </div>
              <span style={{
                padding: "2px 7px", fontSize: 10, fontWeight: 700, borderRadius: 3,
                background: item.type === "topic" ? "#DBEAFE" : "#FEF3C7",
                color: item.type === "topic" ? "#1E40AF" : "#92400E",
              }}>
                {item.type === "topic" ? "TOPIC" : "RSS"}
              </span>
              <div style={{ flex: 1, fontSize: 13 }}>
                {item.type === "topic" ? (
                  <div>
                    <span>
                      <strong>{topicRowLabel(item.id).primary}</strong>
                      {!isEditing && (
                        <button onClick={() => openSubtopicEditor(item.id)} style={{ marginLeft: 8, background: "none", border: "none", color: G, fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                          Edit
                        </button>
                      )}
                      {isEditingThis && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: G, fontWeight: 500 }}>Editing</span>
                      )}
                    </span>
                    {topicRowLabel(item.id).sub && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                        {topicRowLabel(item.id).sub}
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ color: "#555" }}>{(item.feed_url || "").replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
                )}
              </div>
              {(() => {
                const canDelete = item.type !== "topic" || topicCount > 3;
                return (
                  <button onClick={() => canDelete && removeItem(idx)} title={canDelete ? "Remove" : "Minimum 3 topics required"} style={{ background: "none", border: "none", color: canDelete ? "#DC2626" : "#D1D5DB", cursor: canDelete ? "pointer" : "not-allowed", fontSize: 18, fontWeight: 700, padding: "0 4px", lineHeight: 1, opacity: canDelete ? 1 : 0.5 }}>&times;</button>
                );
              })()}
            </div>

            {/* Inline subtopic editor for this row */}
            {isEditingThis && (() => {
              const cat = (CATEGORIES || []).find(c => c.id === expandedTopic);
              if (!cat || !cat.subtopics) return null;
              const draft = draftSubtopics;
              return (
                <div style={{ padding: "14px 16px", marginBottom: 6, background: "#F9FAFB", borderRadius: "0 0 8px 8px", border: `1px solid ${G}`, borderTop: "none" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 10 }}>Subtopics for {cat.label}:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                    {cat.subtopics.map(sub => {
                      const active = draft === "all" || (Array.isArray(draft) && draft.includes(sub.label));
                      return (
                        <button key={sub.label} onClick={() => toggleDraftSubtopic(sub.label)} style={{
                          padding: "4px 10px", fontSize: 12, borderRadius: 14,
                          border: `1px solid ${active ? G : "#ddd"}`,
                          background: active ? "#EBF5FB" : "transparent",
                          color: active ? G : "#888", cursor: "pointer",
                        }}>
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={cancelSubtopicEditor} style={{ background: "transparent", color: "#666", border: `1px solid ${B}`, borderRadius: 6, padding: "7px 16px", fontSize: 12, cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button onClick={saveSubtopicEditor} disabled={subtopicSaving} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: subtopicSaving ? 0.6 : 1 }}>
                      {subtopicSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}

      {/* Add buttons — grayed during edit */}
      <div style={{ display: "flex", gap: 10, marginTop: 16, ...grayStyle }}>
        {unselectedTopics.length > 0 && topicCount < 5 && (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Add topic:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {unselectedTopics.map(c => (
                <button key={c.id} onClick={() => addTopic(c.id)} style={{
                  padding: "4px 12px", fontSize: 12, borderRadius: 14,
                  border: "1px solid #ddd", background: "#fff", color: "#555", cursor: "pointer",
                }}>
                  + {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RSS section — grayed during edit */}
      <div style={grayStyle}>
        {rssCount < maxRss && maxRss > 0 && (
          <div style={{ marginTop: 12 }}>
            {showAddRss ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="url"
                  value={rssUrl}
                  onChange={e => { setRssUrl(e.target.value); setVerifyError(null); }}
                  onKeyDown={e => e.key === "Enter" && handleAddRss()}
                  placeholder="https://example.com/feed.xml"
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, border: "1px solid #ddd", borderRadius: 6 }}
                />
                <button onClick={handleAddRss} disabled={verifying || !rssUrl.trim()} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 600, background: G, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", opacity: verifying ? 0.6 : 1 }}>
                  {verifying ? "Verifying..." : "Add"}
                </button>
                <button onClick={() => { setShowAddRss(false); setRssUrl(""); setVerifyError(null); }} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setShowAddRss(true)} style={{ padding: "6px 14px", fontSize: 13, fontWeight: 600, color: G, background: "transparent", border: `1px solid ${G}`, borderRadius: 6, cursor: "pointer" }}>
                + Add RSS feed
              </button>
            )}
            {verifyError && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 6 }}>{verifyError}</div>}
          </div>
        )}

        <div style={{ fontSize: 12, color: "#888", marginTop: 12, lineHeight: 1.6 }}>
          {rssCount} of {maxRss} RSS feeds.
          {rssCount >= maxRss && maxRss > 0 && <span> RSS feed limit reached for your plan.</span>}
          {maxRss === 0 && <span> RSS feeds require a paid plan.</span>}
        </div>
      </div>
    </div>
  );
}
